import infoStyles from "../components/builder/BuilderInfo.module.css";
import { useEffect, useMemo, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";
import { ButtonStatus, FileType } from "../common/constants";
import PageBuilder from "../components/builder/PageBuilder";
import Select from "../components/Select";
import TextInput from "../components/TextInput";
import Uploader from "../components/Uploader";
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import typo from "../typography.module.css";
import styles from "./ArticleEditor.module.css";
import { PageBuilderProvider } from "../components/builder/PageBuilderContext";
import TagSelector from "../components/TagSelector";


const ArticleEditor = () => {

  const navigate = useNavigate()
  const { articleId } = useParams();
  const [article, setArticle] = useState(null)
  const [tags, setTags] = useState([])
  const [status, setStatus] = useState(
    [
      { id: 0, name: 'Bozza', value: 'draft' },
      { id: 1, name: 'Pubblico', value: 'public' },
      { id: 2, name: 'Nascosto', value: 'hidden' }
    ]
  )

  const [loading, setLoading] = useState(false)
  const [savingAction, setSavingAction] = useState(null)
  const [successAction, setSuccessAction] = useState(null)
  const [lastSavedAt, setLastSavedAt] = useState(null)
  const [uploaderReset, setUploaderReset] = useState(false)
  const [sections, setSections] = useState([])

  useEffect(() => {
    const getArticle = async () => {
      try {
        const w = await api.get(`/admin/articles/${articleId}`)
        setArticle(w)
        setSections(w.content)
        initializeStatus(w.status)
      }
      catch (e) {
        console.error(e)
      }
    }

    const getTags = async (type) => {
      let tags = await api.get(`/admin/tags?type=${type ?? 'general'}`)
      setTags(tags)
    }

    getTags()

    if (articleId !== 'create') {
      getArticle()

    } else if (articleId === "create") {
      setArticle({
        name: "",
        description: "",
        status: "draft",
        thumbnail: "",
        tags: [],
      })
    }
  }, [articleId])


  const initializeStatus = (status) => {
    switch (status) {
      case 'draft':
        setStatus([
          { id: 0, name: 'Bozza', value: 'draft' },
          { id: 1, name: 'Pubblico', value: 'public' },
        ])
        break
      case 'public':
        setStatus([
          { id: 1, name: 'Pubblico', value: 'public' },
          { id: 2, name: 'Nascosto', value: 'hidden' }
        ])
        break
      case 'hidden':
        setStatus([
          { id: 1, name: 'Pubblico', value: 'public' },
          { id: 2, name: 'Nascosto', value: 'hidden' }
        ])
        break
      default: return
    }
  }

  const update = async (page, action = 'publish') => {
    setLoading(true)
    try {

      const body = {
        name: article.name,
        description: article.description,
        thumbnail: article.thumbnail,
        slug: article.slug,
        status: article.status,
        content: page,
        tags: JSON.stringify(article.tags.map(t => t.id))
      }

      const updated = await api.put(`/admin/articles/${article.id}`, body)

      setArticle(updated)
      setSections(updated.content || [])
      initializeStatus(updated.status)
      setSuccessAction(action)
      setLastSavedAt(new Date().toISOString())

      setUploaderReset(!uploaderReset)
    }
    catch (e) {
      console.error(e)
      setSuccessAction(null)
    }
    setLoading(false)

  }

  const create = async (page, action = 'publish') => {
    setLoading(true)
    try {

      const body = {
        name: article.name,
        description: article.description,
        thumbnail: article.thumbnail,
        slug: article.slug,
        status: article.status,
        content: page,
        tags: JSON.stringify(article.tags.map(t => t.id))
      }

      const created = await api.post(`/admin/articles`, body)

      setArticle(created)
      setSections(created.content || [])
      initializeStatus(created.status)
      setSuccessAction(action)
      setLastSavedAt(new Date().toISOString())
      navigate(`/articles/${created.id}`)

      setUploaderReset(!uploaderReset)

    }
    catch (e) {
      console.error(e)
      setSuccessAction(null)
    }
    setLoading(false)
  }

  const save = async (page, activeVersion = 'published', action = 'publish') => {
    setSavingAction(action)
    try {
      if (article.id) {
        await update(page, action)
      }
      else {
        await create(page, action)
      }
    }
    finally {
      setSavingAction(null)
    }
  }

  useEffect(() => {
    if (!successAction) {
      return
    }

    const timeout = setTimeout(() => {
      setSuccessAction(null)
    }, 2000)

    return () => clearTimeout(timeout)
  }, [successAction])

  const saveDisabled = useMemo(() => {
    if (!article) {
      return true
    }

    if (article.status === 'draft') {
      return article.name.trim() === '' || article.slug.trim() === '' || loading || savingAction !== null
    }

    if (article.status === 'public') {
      return article.name.trim() === '' ||
        article.description?.trim() === '' ||
        article.slug.trim() === '' ||
        article.thumbnail === undefined || article.thumbnail === null ||
        article.thumbnail?.trim() === '' ||
        loading ||
        savingAction !== null
    }
  }, [article, loading, savingAction])

  const editBar =
    <div className={infoStyles.edit}>
      {article &&
        <div className={infoStyles.card}>
          {
            articleId !== "create" &&
            <a rel="noreferrer" target="_blank" href={`/articles/${article.id}/preview`} className={infoStyles.previewLink}>APRI PREVIEW</a>
          }
          <div className={infoStyles.editSection}>
            <div className={infoStyles.label}>STATO</div>
            <Select appearance="modern" selected={status.find(s => s.value === article?.status)} options={status} onSelect={(value) => {
              setArticle((prev) => {
                prev.status = value.value
                return { ...prev }
              })
            }} />
          </div>

          <div className={infoStyles.editSection}>

            <div className={infoStyles.label}>NOME</div>
            <TextInput appearance="modern"
              style={{ fontSize: '.75rem' }}
              type="text"
              value={article?.name}
              placeholder="Nome"
              error={article?.name.trim() === ''}
              errorMessage="Campo obbligatorio"
              onKeyUp={(value) => {
                setArticle((prev) => {
                  prev.name = value
                  prev.slug = value?.replaceAll('-', '').replace(/[^a-zA-Z0-9\s-]/g, '') // Rimuovi tutti i caratteri tranne lettere e numeri
                    .replace(/\s+/g, '-') // Sostituisci spazi con trattini
                    .toLowerCase()
                    .trim();
                  return { ...prev }
                })
              }} />
          </div>

          <div className={infoStyles.editSection}>

            <div className={infoStyles.label}>AUTORE</div>
            <TextInput appearance="modern"
              style={{ fontSize: '.75rem' }}
              type="text"
              value={article?.author_name}
              placeholder="Nome"
              error={article?.author_name?.trim() === ''}
              errorMessage="Campo obbligatorio"
              onKeyUp={(value) => {
                setArticle((prev) => {
                  prev.author_name = value
                  return { ...prev }
                })
              }} />
          </div>

          <div className={infoStyles.editSection}>
            <div className={infoStyles.label}>SLUG</div>
            <TextInput appearance="modern"
              style={{ fontSize: '.75rem' }}
              type="text" value={article?.slug}
              error={(article?.slug ?? '').trim() === ''}
              errorMessage="Campo obbligatorio"
              placeholder="Slug"
              onKeyUp={(value) => {
                const newValue = value?.replace(/[^a-zA-Z0-9\s-]/g, '') // Rimuovi tutti i caratteri tranne lettere e numeri
                  .replace(/\s+/g, '-') // Sostituisci spazi con trattini
                  .toLowerCase()
                  .trim(); // Converti tutto in minuscolo
                setArticle((prev) => {
                  prev.slug = newValue
                  return { ...prev }
                })
              }} />
          </div>

          <div className={infoStyles.editSection}>
            <div className={infoStyles.label}>DESCRIZIONE</div>
            <textarea className={infoStyles.description} value={article?.description} onChange={(e) => {
              setArticle((prev) => {
                prev.description = e.target.value
                return { ...prev }
              })
            }} />
          </div>

          <div className={infoStyles.editSection}>
            <div className={infoStyles.label}>THUMBNAIL</div>
            <TextInput appearance="modern"
              style={{ fontSize: '.75rem' }}
              disabled={article?.thumbnail?.startsWith('blob:')}
              type="text" value={article?.thumbnail}
              error={article?.status !== 'draft' && (article?.thumbnail === undefined || article?.thumbnail === null || article?.thumbnail?.trim() === '')}
              errorMessage="Campo obbligatorio"
              placeholder="Thumbnail URL"
              onKeyUp={(value) => {
                setArticle((prev) => {
                  prev.thumbnail = value ? value : ''
                  return { ...prev }
                })
              }} />

            <Uploader
              crop
              aspect={16 / 9}
              shape={'rect'}
              preview={article.thumbnail}
              reset={uploaderReset}
              multiple={false}
              type={FileType.Image}
              iconButtons={true}
              onComplete={(uploads) => {
                setArticle((prev) => {
                  prev.thumbnail = uploads[0].url
                  return { ...prev }
                })
              }}
              onDelete={() => {
                setArticle((prev) => {
                  prev.thumbnail = ""
                  return { ...prev }
                })
              }}
            />
          </div>

          <div className={infoStyles.editSection}>
            <TagSelector
              selected={article.tags}
              options={tags}
              placeholder={"Cerca tag"}
              onAdd={(item) => {
                setArticle((a) => ({ ...a, tags: [...a.tags, item] }))
              }}
              onRemove={(item) => {
                setArticle((a) => {
                  const idx = a.tags.findIndex(t => t.id === item.id)
                  a.tags.splice(idx, 1)
                  return { ...a }
                })
              }}
            />
          </div>
        </div>
      }
    </div>

  return (
    <HeaderFooterLayout hideHeader hideFooter>
      <HelmetProvider>
        <Helmet>
          <title>{article ? `${article.name} - Editor` : "Articolo - Editor"}</title>
        </Helmet>
      </HelmetProvider>
      <div className={styles.container}>
        <div className={styles.preview}>
          {article &&
            <PageBuilderProvider initialPage={sections}>

              <PageBuilder
                element={article}
                lastSavedAt={lastSavedAt || article?.updated_at || article?.created_at}
                customTab={
                  {
                    name: "Info",
                    content: editBar,
                    saveDisabled: saveDisabled
                  }
                }
                onSave={(page, activeVersion, action) => save(page, activeVersion, action)}
                isSaving={savingAction !== null}
                publishButtonStatus={
                  savingAction === 'publish'
                    ? ButtonStatus.Loading
                    : successAction === 'publish'
                      ? ButtonStatus.Success
                      : null
                }
                hideExportToDraft
                onContentChange={(page) => {
                  setSections(page)
                }} />
            </PageBuilderProvider>
          }
        </div>

      </div>
    </HeaderFooterLayout>
  )
}

export default ArticleEditor
